import { FC, memo, useEffect, useRef } from 'react';

import {
  computeProgressPercent,
  type VideoLoopControl,
} from './playbackHelpers';
import { KioskCountdownBadge, ProgressBar, ProgressFill } from './styles';

interface PlaybackProgressBarProps {
  /** Tipo do slot atual — decide a fonte do progresso (vídeo lê o <video>; o
   * resto usa wall-clock). */
  mediaType: string;
  /** Identidade do slot — reinicia a barra ao trocar de mídia (dep do effect). */
  mediaId: string | undefined;
  /** Duração (s) do slot de IMAGEM/stream — denominador do progresso wall-clock. */
  imageDurationSec: number;
  /** Duração (s) usada só no countdown do badge (kiosk ON) — espelha o legado
   * (currentMedia.duration). */
  badgeTotalSec: number;
  /** Wall-clock (ms) de início do slot atual (imagem/stream). */
  startedAtMs: number;
  /** Ref do <video> — progresso de vídeo lê currentTime/duration AO VIVO. */
  videoRef: React.RefObject<HTMLVideoElement>;
  /** Ref do controle de loop do vídeo (cut-short/manual-loop) — lido AO VIVO. */
  loopControlRef: React.MutableRefObject<VideoLoopControl>;
  /** Kiosk locked → badge discreto no canto; unlocked → barra de progresso. */
  kioskLocked: boolean;
}

/**
 * Barra de progresso AUTOSSUFICIENTE (Renato 2026-07-05). Roda o PRÓPRIO
 * requestAnimationFrame e escreve a largura (barra) / o texto (badge) DIRETO no
 * DOM via ref — NUNCA chama setState. Resultado: o <Stream> pai deixa de
 * re-renderizar por progresso.
 *
 * Motivação (diagnóstico do gargalo do Tab A7): antes, `setVideoProgress` era
 * chamado ~60×/s (rAF, durante imagem) e ~4×/s (timeupdate, durante vídeo) num
 * useState do Stream — re-renderizando a árvore INTEIRA de ~4000 linhas
 * (incl. painel de debug sempre montado) só pra mover a barrinha. Isso mantinha
 * o WebView repintando ~48fps sustentados (gfxinfo: 93% high-input-latency),
 * roubando main-thread do decoder/transições/screenshot.
 *
 * memo() garante que o componente só re-renderiza quando a MÍDIA ou o estado de
 * kiosk muda (não a cada tick) — entre trocas de mídia, o Stream pode
 * re-renderizar por outros motivos (pollings) sem resetar a largura da barra.
 * Refs (videoRef/loopControlRef) são estáveis → não quebram o memo.
 */
const PlaybackProgressBar: FC<PlaybackProgressBarProps> = ({
  mediaType,
  mediaId,
  imageDurationSec,
  badgeTotalSec,
  startedAtMs,
  videoRef,
  loopControlRef,
  kioskLocked,
}) => {
  const fillRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let raf = 0;
    let to: ReturnType<typeof setTimeout> | undefined;
    // Só vídeo precisa de rAF (~60fps): lê o currentTime AO VIVO e a barra
    // acompanha suave. Imagem/stream usa wall-clock (Date.now/startedAt), então
    // 250ms basta. Rodar rAF a 60fps escrevendo num layer `will-change: transform`
    // SOBRE uma imagem estática mantinha uma camada de composição mutada 60×/s →
    // churn contínuo de GPU/compositor no WebKit Tizen (dreno de memória nativa no
    // loop de 1 imagem). Vídeo segue em rAF (inalterado).
    const isVideo = mediaType === 'video';

    const tick = () => {
      // Lógica de % extraída pro playbackHelpers (testável sem rAF/DOM).
      const pct = computeProgressPercent({
        mediaType,
        video: videoRef.current,
        loopControl: loopControlRef.current,
        imageDurationSec,
        startedAtMs,
        nowMs: Date.now(),
      });
      // scaleX (compositor/GPU) em vez de width (layout+paint) — frame barato no
      // A7 (Renato 2026-07-05). transform-origin: left no CSS → cresce da esquerda.
      if (fillRef.current)
        fillRef.current.style.transform = `scaleX(${pct / 100})`;
      if (badgeRef.current && badgeTotalSec > 0) {
        const remaining = Math.max(
          0,
          Math.ceil(((100 - pct) / 100) * badgeTotalSec),
        );
        badgeRef.current.textContent = `${remaining}s`;
      }
      if (isVideo) raf = requestAnimationFrame(tick);
      else to = setTimeout(tick, 250);
    };

    // Reset no início de cada mídia (a barra começa vazia).
    if (fillRef.current) fillRef.current.style.transform = 'scaleX(0)';
    if (isVideo) raf = requestAnimationFrame(tick);
    else to = setTimeout(tick, 250);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (to) clearTimeout(to);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mediaId,
    mediaType,
    imageDurationSec,
    badgeTotalSec,
    startedAtMs,
    kioskLocked,
  ]);

  // Kiosk locked: ProgressBar é ruído visual pro cliente final. Countdown
  // discreto no canto (só quando há duração) — mesma semântica do legado.
  if (kioskLocked) {
    return badgeTotalSec > 0 ? <KioskCountdownBadge ref={badgeRef} /> : null;
  }

  return (
    <ProgressBar>
      <ProgressFill ref={fillRef} />
    </ProgressBar>
  );
};

export default memo(PlaybackProgressBar);
